import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PlatformTermsReacceptanceStatus } from '../../models/legal/platform-terms-reacceptance-status';

/** Error code the API returns when the version being accepted is no longer the one in force. */
export const PLATFORM_TERMS_VERSION_MISMATCH = 'ERR_PLATFORM_TERMS_VERSION_MISMATCH';

/**
 * P2P-28 · F0.3 — the platform T&C login-time re-acceptance gate.
 *
 * `{userId}` is filled in by the access-token interceptor. Both endpoints are owner-guarded
 * server-side and return the BaseApiController `{ result }` envelope.
 */
@Injectable({ providedIn: 'root' })
export class LegalConsentService {
  private readonly route = environment.apiServer + 'Api/v1/Users/{userId}/legal';

  constructor(private readonly http: HttpClient) {}

  getPlatformTermsReacceptanceStatus(): Observable<PlatformTermsReacceptanceStatus> {
    return this.http
      .get<{ result: PlatformTermsReacceptanceStatus }>(
        this.route + '/platform-terms/reacceptance-status',
      )
      .pipe(map((res) => res.result));
  }

  /**
   * Records acceptance of the platform T&C version the user was shown (`versionId`, from
   * {@link getPlatformTermsReacceptanceStatus}) — never "whatever is latest when this lands".
   * If a newer version has been published since, the server rejects with
   * {@link PLATFORM_TERMS_VERSION_MISMATCH} and records nothing, so the caller must re-fetch
   * the status and show the new text. Idempotent server-side.
   */
  acceptPlatformTerms(versionId: number): Observable<void> {
    return this.http
      .post<unknown>(this.route + '/platform-terms/reacceptance', {
        legalDocumentVersionId: versionId,
      })
      .pipe(map(() => undefined));
  }
}
